import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Button, Space, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import apiClient from '../services/api';

const { Title } = Typography;

interface FreeUser5Patients {
  account_id: string;
  full_name: string;
  email: string;
  specialty: string;
  prefecture: string;
  clinic_name: string;
  vip_status: number;
  created_at: string;
  patient_count: number;
  active_days?: number;
}

interface FreeUsers5PatientsResponse {
  free_users: FreeUser5Patients[];
  total_count: number;
}

const FullscreenFreeUsers5Patients: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<FreeUser5Patients[]>([]);

  useEffect(() => {
    fetchFreeUsers5Patients();
  }, []);

  const fetchFreeUsers5Patients = async () => {
    try {
      setLoading(true);
      const response: FreeUsers5PatientsResponse = await apiClient.get('/api/analytics/free-users-5-patients?limit=1000');
      
      // 设置基本数据，稍后异步获取活跃天数
      setData(response.free_users.map(user => ({ ...user, active_days: 0 })));
      
      // 异步获取活跃天数
      response.free_users.forEach(async (user, index) => {
        try {
          const activeDaysResponse: any = await apiClient.get(`/api/analytics/user-active-days/${user.account_id}`);
          setData(prevData => 
            prevData.map((item, i) => 
              i === index ? { ...item, active_days: activeDaysResponse.active_days || 0 } : item
            )
          );
        } catch (error) {
          console.error(`Failed to get active days for user ${user.account_id}:`, error);
        }
      });
    } catch (error) {
      console.error('Failed to fetch free users with 5 patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<FreeUser5Patients> = [
    {
      title: '氏名',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 180,
      render: (name: string, record: FreeUser5Patients) => (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginBottom: '4px'
          }}>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
              {name || `ユーザー${record.account_id.slice(-8)}`}
            </span>
            <Tag 
              color={VIP_STATUS_COLORS[record.vip_status as VipStatus]}
              style={{ fontSize: '10px', padding: '2px 6px' }}
            >
              {VIP_STATUS_LABELS[record.vip_status as VipStatus]}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'メールアドレス',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: (email: string) => (
        <span style={{ fontSize: '12px', wordBreak: 'break-all' }}>
          {email}
        </span>
      ),
    },
    {
      title: 'クリニック',
      dataIndex: 'clinic_name',
      key: 'clinic_name',
      width: 180,
      ellipsis: true,
      render: (clinic: string, record: FreeUser5Patients) => (
        <span 
          style={{ 
            color: '#1890ff', 
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '12px'
          }}
          onClick={() => {
            if (clinic && clinic !== 'なし') {
              const searchQuery = record.prefecture ? 
                `${clinic} ${record.prefecture}` : 
                clinic;
              const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`;
              window.open(googleSearchUrl, '_blank');
            }
          }}
        >
          {clinic || 'なし'}
        </span>
      ),
    },
    {
      title: '都道府県',
      dataIndex: 'prefecture',
      key: 'prefecture',
      width: 100,
      render: (prefecture: string) => (
        <span style={{ fontSize: '12px' }}>
          {prefecture || '不明'}
        </span>
      ),
    },
    {
      title: '専業',
      dataIndex: 'specialty',
      key: 'specialty',
      width: 120,
      ellipsis: true,
      render: (specialty: string) => (
        <span style={{ fontSize: '12px' }}>
          {specialty || '不明'}
        </span>
      ),
    },
    {
      title: 'アクティブ日数',
      dataIndex: 'active_days',
      key: 'active_days',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.active_days || 0) - (b.active_days || 0),
      render: (days: number) => (
        <span style={{ fontSize: '12px', fontWeight: 'bold', color: days > 0 ? '#1890ff' : '#999' }}>
          {days || 0}日
        </span>
      ),
    },
    {
      title: '患者数',
      dataIndex: 'patient_count',
      key: 'patient_count',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.patient_count - b.patient_count,
      render: (count: number) => (
        <span style={{ 
          fontSize: '12px', 
          fontWeight: 'bold', 
          color: count >= 5 ? '#ff4d4f' : '#1890ff' 
        }}>
          {count}名
        </span>
      ),
    },
    {
      title: '登録日',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => (
        <span style={{ fontSize: '11px' }}>
          {date || '不明'}
        </span>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Button 
          icon={<ArrowLeftOutlined />}
          onClick={() => window.close()}
          type="primary"
        >
          閉じる
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          患者数５名フリーユーザー
        </Title>
        <span style={{ color: '#666', fontSize: '16px' }}>
          ({data.length}件)
        </span>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px' }}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="account_id"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} を表示、全 ${total} 件`,
              pageSizeOptions: ['20', '50', '100', '200'],
            }}
            scroll={{ x: 1200 }}
            size="small"
            style={{ fontSize: '12px' }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default FullscreenFreeUsers5Patients;